import { CalculatorOutputs, CalculatorInputs } from '../../types/calculator';
import { formatNumber, formatHours, formatCurrency, formatFTE, formatWeight, formatVolume, formatEnergy } from '../../utils/formatters';

interface PdfContentProps {
  scaledOutputs: CalculatorOutputs;
  timePeriod: string;
  inputs: CalculatorInputs;
}

// Format date as day/month/year
const formatDate = (date: Date): string => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Generate complete HTML document for Puppeteer rendering
export const generatePdfHtml = ({ scaledOutputs, timePeriod, inputs }: PdfContentProps): string => {
  const timePeriodLabel =
    timePeriod === 'day' ? 'Daily' :
    timePeriod === 'week' ? 'Weekly' :
    timePeriod === 'month' ? 'Monthly' :
    timePeriod === 'quarter' ? 'Quarterly' : 'Yearly';

  const formattedDate = formatDate(new Date());

  const cupsLabel =
    timePeriod === 'day' ? 'Cups Served Today' :
    timePeriod === 'week' ? 'Cups Served This Week' :
    timePeriod === 'month' ? 'Cups Served This Month' :
    timePeriod === 'quarter' ? 'Cups Served This Quarter' : 'Cups Served This Year';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Lora:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', system-ui, sans-serif;
          background-color: #FDF7EF;
          padding: 24px;
          color: #1A202C;
        }

        .font-serif {
          font-family: 'Lora', Georgia, serif;
        }

        .card {
          background-color: white;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
        }

        .card-title {
          font-size: 14px;
          font-family: 'Lora', Georgia, serif;
          font-weight: 600;
          color: #1A202C;
        }

        .grid-2 {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .grid-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .metric-box {
          padding: 12px;
          border-radius: 8px;
          background-color: #FAF0EC;
        }

        .metric-content {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .metric-label {
          font-size: 11px;
          color: #A2B7BB;
          margin: 0;
        }

        .metric-value {
          font-size: 16px;
          font-weight: bold;
          color: #6F8D90;
          margin: 0;
        }

        .icon {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .icon-orange { color: #C35E38; }
        .icon-emerald { color: #10B981; }
        .icon-sky { color: #0EA5E9; }
        .icon-sage { color: #6F8D90; }
        .icon-maroon { color: #8B4513; }

        .header {
          text-align: center;
          margin-bottom: 24px;
        }

        .header h1 {
          font-size: 24px;
          font-family: 'Lora', Georgia, serif;
          font-weight: bold;
          color: #1A202C;
          margin-bottom: 8px;
        }

        .header p {
          font-size: 14px;
          color: #6F8D90;
        }

        .footer {
          margin-top: 24px;
          padding-top: 16px;
          border-top: 1px solid #E5E7EB;
          text-align: center;
        }

        .footer p {
          font-size: 11px;
          color: #A2B7BB;
        }

        .no-margin-bottom {
          margin-bottom: 0;
        }

        .page-break {
          page-break-after: always;
        }

        .input-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .input-item {
          padding: 16px;
          background-color: #FAF0EC;
          border-radius: 8px;
        }

        .input-label {
          font-size: 11px;
          color: #6F8D90;
          font-weight: 600;
          margin-bottom: 4px;
        }

        .input-value {
          font-size: 20px;
          font-weight: bold;
          color: #6F8D90;
        }

        .input-helper {
          font-size: 10px;
          color: #A2B7BB;
          margin-top: 4px;
        }

        .page-title {
          font-size: 12px;
          color: #A2B7BB;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 16px;
          padding-top: 24px;
        }
      </style>
    </head>
    <body>
      <!-- PAGE 1: Your Workplace Inputs -->
      <div class="header">
        <h1>Workplace Coffee ROI Report</h1>
        <p>${timePeriodLabel} Summary • Generated ${formattedDate}</p>
      </div>

      <p class="page-title">Your Workplace Configuration</p>

      <div class="card">
        <div class="card-header">
          <svg class="icon icon-orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
          <h3 class="card-title">Your Workplace</h3>
        </div>
        <div class="input-grid">
          <div class="input-item">
            <p class="input-label">Number of Employees</p>
            <p class="input-value">${formatNumber(inputs.employeeCount, 0)}</p>
            <p class="input-helper">Total employees in workplace</p>
          </div>
          <div class="input-item">
            <p class="input-label">Average Annual Salary</p>
            <p class="input-value">${formatCurrency(inputs.avgSalary)}</p>
            <p class="input-helper">Average employee salary (AUD)</p>
          </div>
          <div class="input-item">
            <p class="input-label">Average Break Duration</p>
            <p class="input-value">${inputs.breakDuration} min</p>
            <p class="input-helper">Time spent leaving for coffee</p>
          </div>
          <div class="input-item">
            <p class="input-label">Daily Cups per Employee</p>
            <p class="input-value">${formatNumber(inputs.cupsPerEmployee, 1)}</p>
            <p class="input-helper">Average cups per day</p>
          </div>
          <div class="input-item">
            <p class="input-label">Office Days per Week</p>
            <p class="input-value">${inputs.officeDaysPerWeek} days</p>
            <p class="input-helper">Days staff come into office</p>
          </div>
          <div class="input-item">
            <p class="input-label">Company Subsidy</p>
            <p class="input-value">${formatCurrency(inputs.subsidyPercent)}</p>
            <p class="input-helper">Amount subsidised per coffee</p>
          </div>
        </div>
      </div>

      <div class="page-break"></div>

      <!-- PAGE 2: Results and Metrics -->
      <p class="page-title">${timePeriodLabel} Impact Summary</p>

      <!-- Coffee Summary Card -->
      <div class="card">
        <div class="card-header">
          <svg class="icon icon-orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <h3 class="card-title">${timePeriodLabel} Coffee Summary</h3>
        </div>
        <div class="grid-2">
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 8h1a4 4 0 1 1 0 8h-1"></path>
                <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8z"></path>
                <line x1="6" y1="2" x2="6" y2="4"></line>
                <line x1="10" y1="2" x2="10" y2="4"></line>
                <line x1="14" y1="2" x2="14" y2="4"></line>
              </svg>
              <div>
                <p class="metric-label">${cupsLabel}</p>
                <p class="metric-value">${formatNumber(scaledOutputs.totalCupsPerDay, 0)}</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <div>
                <p class="metric-label">Total Break Time</p>
                <p class="metric-value">${formatHours(scaledOutputs.employeeValue.timeSavedHours)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Employee Value Card -->
      <div class="card">
        <div class="card-header">
          <svg class="icon icon-orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="1" x2="12" y2="23"></line>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
          </svg>
          <h3 class="card-title">Employee Value Proposition</h3>
        </div>
        <div class="grid-2">
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <div>
                <p class="metric-label">Connection Time</p>
                <p class="metric-value">${formatHours(scaledOutputs.employeeValue.timeSavedHours)}</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                <path d="M12 9v6"></path>
                <path d="M9 12h6"></path>
              </svg>
              <div>
                <p class="metric-label">Employee Savings</p>
                <p class="metric-value">${formatCurrency(scaledOutputs.employeeValue.employeeSavings)}</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              <div>
                <p class="metric-label">Productivity Savings</p>
                <p class="metric-value">${formatCurrency(scaledOutputs.employeeValue.productivityValue)}</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
              <div>
                <p class="metric-label">Annualised FTE</p>
                <p class="metric-value">${formatFTE(scaledOutputs.employeeValue.fteRecovered)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sustainability Card -->
      <div class="card">
        <div class="card-header">
          <svg class="icon icon-orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"></path>
            <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"></path>
          </svg>
          <h3 class="card-title">Sustainability</h3>
        </div>
        <div class="grid-3">
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 8h1a4 4 0 1 1 0 8h-1"></path>
                <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V8z"></path>
                <line x1="6" y1="2" x2="6" y2="4"></line>
                <line x1="10" y1="2" x2="10" y2="4"></line>
                <line x1="14" y1="2" x2="14" y2="4"></line>
              </svg>
              <div>
                <p class="metric-label">Cups Saved</p>
                <p class="metric-value">${formatNumber(Math.round(scaledOutputs.sustainability.cupsSaved), 0)}</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2"></path>
                <path d="M9.6 4.6A2 2 0 1 1 11 8H2"></path>
                <path d="M12.6 19.4A2 2 0 1 0 14 16H2"></path>
              </svg>
              <div>
                <p class="metric-label">CO₂ Saved</p>
                <p class="metric-value">${formatWeight(scaledOutputs.sustainability.co2Avoided)}</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
              <div>
                <p class="metric-label">Energy Saved</p>
                <p class="metric-value">${formatEnergy(scaledOutputs.sustainability.energySaved)}</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
              </svg>
              <div>
                <p class="metric-label">Water Saved</p>
                <p class="metric-value">${formatVolume(scaledOutputs.sustainability.waterSaved)}</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 6 21 18 21 19 6 21 6"></polyline>
                <line x1="8" y1="6" x2="8" y2="3" stroke-linecap="round"></line>
                <line x1="16" y1="6" x2="16" y2="3" stroke-linecap="round"></line>
                <line x1="12" y1="11" x2="12" y2="17"></line>
                <line x1="9" y1="14" x2="15" y2="14"></line>
              </svg>
              <div>
                <p class="metric-label">Waste Diverted</p>
                <p class="metric-value">${formatWeight(scaledOutputs.sustainability.wasteDiverted)}</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M7 19H4.815a1.83 1.83 0 0 1-1.57-.881 1.785 1.785 0 0 1-.004-1.784L7.196 9.5"></path>
                <path d="M11 19h8.203a1.83 1.83 0 0 0 1.556-.89 1.784 1.784 0 0 0 0-1.775l-1.226-2.12"></path>
                <path d="m14 16-3 3 3 3"></path>
                <path d="M8.293 13.596 7.196 9.5 3.1 10.598"></path>
                <path d="m9.344 5.811 1.093-1.892A1.83 1.83 0 0 1 11.985 3a1.784 1.784 0 0 1 1.546.888l3.943 6.843"></path>
                <path d="m13.378 9.633 4.096 1.098 1.097-4.096"></path>
              </svg>
              <div>
                <p class="metric-label">Plastic Saved</p>
                <p class="metric-value">${formatWeight(scaledOutputs.sustainability.plasticSaved)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Real World Impact Card -->
      <div class="card no-margin-bottom">
        <div class="card-header">
          <svg class="icon icon-orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="2" y1="12" x2="22" y2="12"></line>
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
          </svg>
          <h3 class="card-title">Real World Impact</h3>
        </div>
        <div class="grid-3">
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="m17 14 3 3.3a1 1 0 0 1-.7 1.7H4.7a1 1 0 0 1-.7-1.7L7 14h-.3a1 1 0 0 1-.7-1.7L9 9h-.2A1 1 0 0 1 8 7.3L12 3l4 4.3a1 1 0 0 1-.8 1.7H15l3 3.3a1 1 0 0 1-.7 1.7H17Z"></path>
                <path d="M12 22v-3"></path>
              </svg>
              <div>
                <p class="metric-label">Trees Planted</p>
                <p class="metric-value">${formatNumber(scaledOutputs.sustainability.treesEquivalent, 1)}</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"></path>
                <circle cx="7" cy="17" r="2"></circle>
                <path d="M9 17h6"></path>
                <circle cx="17" cy="17" r="2"></circle>
              </svg>
              <div>
                <p class="metric-label">CO₂ Offset</p>
                <p class="metric-value">${formatNumber(Math.round(scaledOutputs.sustainability.carKmOffset), 0)} km</p>
              </div>
            </div>
          </div>
          <div class="metric-box">
            <div class="metric-content">
              <svg class="icon icon-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 6 21 18 21 19 6 21 6"></polyline>
                <line x1="8" y1="6" x2="8" y2="3" stroke-linecap="round"></line>
                <line x1="16" y1="6" x2="16" y2="3" stroke-linecap="round"></line>
                <line x1="12" y1="11" x2="12" y2="17"></line>
                <line x1="9" y1="14" x2="15" y2="14"></line>
              </svg>
              <div>
                <p class="metric-label">Waste Avoided</p>
                <p class="metric-value">${formatWeight(scaledOutputs.sustainability.wasteAvoided)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="footer">
        <p>Generated by Coffee On Cue • ${formattedDate}</p>
      </div>
    </body>
    </html>
  `;
};
